import React, { useContext, useEffect, useState } from 'react';
import { Box, Divider, InputBase, Pagination } from '@mui/material';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import {
  IContact,
  IRecipientListContactsQueryVariables,
  useContactsByInterestsQuery,
  useRecipientListContactsQuery
} from '../../../generated/graphql';
import { RecipientListType } from '../../../models/recipientList';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import SearchInput from './SearchInput';
import { Colors } from '../../../utils/theme';
import './index.css';
import RecipientItem from './RecipientItem';

export type Recipient = IContact & {
  isNew?: boolean;
};

const recipientsPerPage = 9;

const RecipientsStep: React.FunctionComponent = () => {
  const { dispatch, state } = useContext(AppContext);
  const {
    contactTitleIds,
    companyTypeIds,
    membershipTypeIds,
    interestIds,
    countryIds,
    excludedContactIds
  } = state.selectedRecipientList as RecipientListType;
  const variablesObj: IRecipientListContactsQueryVariables = {
    countryIds: countryIds.map((c) => parseInt(c)),
    interestIds: interestIds.map((i) => parseInt(i)),
    membershipTypeIds: membershipTypeIds.map((m) => parseInt(m)),
    companyTypeIds: companyTypeIds.map((ct) => parseInt(ct)),
    contactTitleIds: contactTitleIds.map((ct) => parseInt(ct))
  };
  if (state.selectedRecipientList?.id) {
    variablesObj.recipientListId = parseInt(state.selectedRecipientList?.id as string);
  }
  const recipientListContacts = useRecipientListContactsQuery({
    variables: {
      ...variablesObj
    },
    fetchPolicy: 'no-cache'
  });
  const additionalContactsQuery = useContactsByInterestsQuery({
    variables: {
      interestIds: interestIds.map((i) => parseInt(i))
    },
    fetchPolicy: 'no-cache'
  });
  const [additionalContacts, setAdditionalContacts] = useState<IContact[]>([]);
  const [recipientList, setRecipientList] = useState<Recipient[]>([]);
  const [displayedRecipients, setDisplayedRecipients] = useState<Recipient[]>([]);
  const [options, setOptions] = useState<IContact[]>([]);
  const [key, setKey] = useState('0');
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [excludedList, setExcludedList] = useState<number[]>(excludedContactIds);

  useEffect(() => {
    if (excludedList != excludedContactIds) {
      dispatch({
        type: Actions.SetRecipientListExcludedContacts,
        payload: {
          excludedContactIds: excludedList
        }
      });
    }
  }, [excludedList]);

  useEffect(() => {
    if (additionalContactsQuery.data && additionalContactsQuery.data.result) {
      const contacts = additionalContactsQuery.data.result as IContact[];
      setAdditionalContacts(contacts);
      if (recipientListContacts.data && recipientListContacts.data.result) {
        const res = recipientListContacts.data.result as Recipient[];
        const resIds = res.map((r) => r.id);
        setOptions(contacts.filter((i) => !resIds.includes(i.id)));
      } else {
        setOptions(contacts);
      }
    }
  }, [additionalContactsQuery.data]);

  useEffect(() => {
    if (recipientListContacts.data && recipientListContacts.data.result) {
      const res = recipientListContacts.data.result as Recipient[];
      const resIds = res.map((r) => r.id);
      setRecipientList(res);
      setDisplayedRecipients(res.slice(0, recipientsPerPage));
      setPages(res.length > recipientsPerPage ? Math.ceil(res.length / recipientsPerPage) : 0);
      setOptions(additionalContacts.filter((i) => !resIds.includes(i.id)));
    }
  }, [recipientListContacts.data]);

  const handleAddRecipient = (_: unknown, newValue: Recipient | null) => {
    if (newValue) {
      const isNew = !excludedList.includes(parseInt(newValue.id));
      if (!isNew) {
        setExcludedList(excludedList.filter((i) => i !== parseInt(newValue.id)));
      } else {
        dispatch({
          type: Actions.RecipientListIncludeContact,
          payload: {
            contactId: parseInt(newValue.id)
          }
        });
      }
      const newData = [{ ...newValue, isNew }, ...recipientList];
      setRecipientList(newData);
      const filtered = filterRecipients(newData, searchText);
      if (newData.length > recipientsPerPage) {
        setDisplayedRecipients(filtered.slice(0, recipientsPerPage));
      } else {
        setDisplayedRecipients(filtered);
      }
      setOptions(options.filter((o) => o.id !== newValue.id));
      setKey(newValue.id);
      setPages(
        filtered.length > recipientsPerPage ? Math.ceil(newData.length / recipientsPerPage) : 0
      );
    } else {
      setKey('0');
    }
  };

  const handleRemoveFromList = (value: Recipient) => {
    setKey('0');
    if (!value.isNew) {
      if (!state.selectedRecipientList?.includedContactIds.includes(parseInt(value.id))) {
        setExcludedList([parseInt(value.id), ...excludedList]);
      }
    }

    if (state.selectedRecipientList?.includedContactIds.includes(parseInt(value.id))) {
      dispatch({
        type: Actions.RecipientListRemoveIncludedContact,
        payload: {
          contactId: parseInt(value.id)
        }
      });
    }

    const newData = recipientList.filter((r) => r.id !== value.id);
    const filtered = filterRecipients(newData, searchText);
    setRecipientList(newData);
    setDisplayedRecipients(filtered.slice(0, recipientsPerPage));
    setPages(
      filtered.length > recipientsPerPage ? Math.ceil(filtered.length / recipientsPerPage) : 0
    );
    setOptions([value, ...options]);
    if (newData.length <= recipientsPerPage) {
      setCurrentPage(1);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);
    const filtered = filterRecipients(recipientList, text);
    setPages(
      filtered.length > recipientsPerPage ? Math.ceil(filtered.length / recipientsPerPage) : 0
    );
    setDisplayedRecipients(filtered.slice(0, recipientsPerPage));
  };

  const filterRecipients = (input: Recipient[], text: string): Recipient[] => {
    if (text.length === 0) {
      return input;
    } else {
      const loweredText = text.toLowerCase();
      const filtered = input.filter(
        (r) =>
          r.fullName.toLowerCase().includes(loweredText) ||
          r.email?.toLowerCase().includes(loweredText) ||
          r.titleInternal.name.toLowerCase().includes(loweredText)
      );
      return filtered;
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    const start = (value - 1) * recipientsPerPage;
    setDisplayedRecipients(recipientList.slice(start, start + recipientsPerPage));
    setCurrentPage(value);
  };

  return (
    <Box className="step-container">
      <Box className="section-header" sx={{ height: '80px', justifyContent: 'space-between' }}>
        <SearchInput
          id={'overview-step-search-input'}
          filter={handleSearchChange}
          sx={{ width: '40%' }}
        />
        <Divider
          orientation="vertical"
          sx={{ background: Colors.white, width: 2, height: '60%', borderColor: Colors.white }}
        />
        <AutocompleteComponent
          id="add-recipient-autocomplete"
          label=""
          onChange={handleAddRecipient}
          values={undefined}
          options={options}
          getOptionLabel={(option: IContact) => option.fullName}
          additionalStyle={{ width: '40%' }}
          isWhite
        />
      </Box>
      <Box className="step-content-container">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: '5px' }}>
          <InputBase
            value={'RECIPIENTS'}
            style={{ fontSize: 14, fontWeight: 'bold', color: Colors.main }}
          />
          <span style={{ fontSize: 14, color: Colors.gray }}>
            {`${recipientList.length}` +
              (recipientList.length === 1 ? ' Recipient' : ' Recipients')}
          </span>
        </Box>
        <Box display="flex" gap="8px" flexDirection="column">
          {displayedRecipients.map((rec, index) => (
            <RecipientItem
              key={`${index}-${rec.id}`}
              recipient={rec}
              removeFromList={handleRemoveFromList}
            />
          ))}
        </Box>
        {pages > 0 && (
          <Box sx={{ mt: '10px' }}>
            <Pagination
              id="recipients-pagination"
              data-testid="recipients-pagination"
              style={{ margin: 'auto' }}
              count={pages}
              page={currentPage}
              color="primary"
              onChange={handlePageChange}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecipientsStep;
